import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { Space, Tooltip, Button, Tag } from 'antd'
import { ArrowRightOutlined, EditOutlined, CarOutlined } from '@ant-design/icons'
import {
  NoteEditButton,
  NoteSaveButton,
  NoteTextArea,
  TimeLineContentHeaderWrapper,
  TimeLineContentWrapper,
} from '../../../styles/styles'
import { ActivityNote } from '../../../types/types'
import TimelineItemHeader from '../activity-timeline-item-header/activity-timeline-item-header'
import { Avatar } from '../../../../../../ui/Avatar'
import NoteStatic from './note-static/note-static'
import { HStack } from '../../../../../../ui/Stack'
import { ActivityTimelineContext } from '../../../context/activity-timeline-context'
import { setNote } from '../../../../../../views/modal-add-notes-new/api/setNote'
import { TimelineItemProps } from '../activity-timeline-Item'
import { Stage } from '../../../../../activity-timeline/styles'

const autoSize = { minRows: 7, maxRows: 12 }

type ActivityTimelineNoteType = Omit<TimelineItemProps, 'data'>
interface ActivityTimelineNoteProps extends ActivityTimelineNoteType {
  className?: string
  data?: ActivityNote
}

const ActivityTimelineNote: React.FC<ActivityTimelineNoteProps> = memo(
  (props: ActivityTimelineNoteProps) => {
    const { date, user, data, index, scopeDate, type } = props
    const [isEditNote, setIsEditNote] = useState<boolean>(false)
    const [localText, setLocalText] = useState(data?.note?.text)
    const { card_id, card_type, updateTimeline } = useContext(ActivityTimelineContext)

    const noteName = useMemo(() => {
      if (!data?.noteType) return 'Заметка'

      switch (data.noteType) {
        case 'region-change':
          return 'Регион изменен'
        case 'manager-change':
          return 'Изменение ФИО менеджера'
        case 'repeated-appeal':
          return 'Повторное обращение'
        case 'delivery-notification':
          return 'Уведомление о доставке'
        case 'windraw-pickup':
          return 'Самовывоз WinDraw'
        case 'quiz-answers':
          return 'Ответы на квиз'
        default:
          return data?.title || 'Заметка'
      }
    }, [data])

    const changeNote = useCallback(async () => {
      if (card_id && card_type) {
        await setNote({
          note: localText || '',
          card_id: card_id,
          card_type: card_type,
          id: data?.id,
        })
          .then(() => {
            if (updateTimeline) {
              updateTimeline?.({
                data: {
                  note: {
                    ...data?.note,
                    text: localText,
                  },
                },
                type: type,
                date: scopeDate,
                index,
              })
            }
          })
          .finally(() => {
            setIsEditNote(false)
          })
      }
      setIsEditNote(false)
    }, [data, localText, setIsEditNote, card_id, card_type, updateTimeline])

    const cancelEditNote = useCallback(async () => {
      setIsEditNote(false)
      setLocalText(data?.note?.text)
    }, [setIsEditNote, data])

    return (
      <>
        <TimeLineContentWrapper>
          {data?.noteType === 'delivery-notification' && card_type !== 'delivery' && (
            <Space style={{ justifyContent: 'flex-end', marginBottom: '12px' }}>
              <Tag icon={<CarOutlined />} color="green" style={{ marginRight: 0 }}>
                Доставка
              </Tag>
            </Space>
          )}
          <TimeLineContentHeaderWrapper>
            <Space>
              <TimelineItemHeader date={date} title={noteName} />
              {!data?.readonly && (
                <NoteEditButton disabled={isEditNote} onClick={() => setIsEditNote(true)}>
                  <EditOutlined />
                </NoteEditButton>
              )}
            </Space>
            <Space direction={'vertical'}>
              {user && data?.noteType === 'region-change' && (
                <Space>
                  <Avatar src={user?.icon} />
                  <Tooltip placement="top" title="Автор">
                    <span>{user?.name}</span>
                  </Tooltip>
                </Space>
              )}
              {user &&
                data?.noteType !== 'region-change' &&
                data?.noteType !== 'manager-change' && (
                  <Space>
                    <Tooltip placement="top" title="Автор">
                      <span>{user?.name}</span>
                    </Tooltip>
                  </Space>
                )}
            </Space>
          </TimeLineContentHeaderWrapper>
          {data?.noteType === 'region-change' && (
            <>
              <Space>
                <Stage color={'#e8f1fb'}>{data.note?.from}</Stage>
                <ArrowRightOutlined style={{ color: '#b1bac3' }} />
                <Stage color={'#e8f1fb'}>{data.note?.to}</Stage>
              </Space>
            </>
          )}
          {data?.noteType === 'manager-change' && (
            <NoteStatic type={data.noteType} data={data?.note} />
          )}
          {data?.noteType && data?.noteType === 'repeated-appeal' && <>{data?.note?.text}</>}
          {data?.noteType &&
            (data?.noteType === 'delivery-notification' ||
              data?.noteType === 'windraw-pickup' ||
              data?.noteType === 'quiz-answers') && <NoteStatic data={data?.note} />}
          {(!data?.noteType || data?.noteType === 'note') && isEditNote && (
            <>
              <NoteTextArea
                autoSize={autoSize}
                value={localText}
                onChange={({ target }) => setLocalText(target.value)}
              />
              <HStack gap={'16'} align="center" justify={'start'}>
                <NoteSaveButton onClick={changeNote}>Сохранить</NoteSaveButton>
                <Button type="default" onClick={cancelEditNote}>
                  Отменить
                </Button>
              </HStack>
            </>
          )}
          {(!data?.noteType || data?.noteType === 'note') && <NoteStatic data={data?.note} />}
        </TimeLineContentWrapper>
      </>
    )
  },
)

ActivityTimelineNote.displayName = 'ActivityTimelineNote'
export default ActivityTimelineNote
